import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { Integration } from '../types/integration';
import { GamifyIcon } from '../../../../../components/GamifyIcon';

interface IntegrationCardProps {
  integration: Integration;
  onLearnMore: (integration: Integration) => void;
  onGetStarted: (integration: Integration) => void;
}

export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  onLearnMore,
  onGetStarted,
}) => {
  const getStatusColor = (status: Integration['status']) => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'disabled':
        return 'error';
      case 'available':
        return 'primary';
      case 'beta':
        return 'warning';
      case 'coming-soon':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: Integration['status']) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'disabled':
        return 'Disabled';
      case 'available':
        return 'Available';
      case 'beta':
        return 'Beta';
      case 'coming-soon':
        return 'Coming Soon';
      default:
        return status;
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              mr: 2,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& img': {
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              },
            }}
          >
            {integration.imageUrl ? (
              <img
                src={integration.imageUrl}
                alt={`${integration.name} logo`}
              />
            ) : (
              <GamifyIcon icon={integration.icon} color="#323232" />
            )}
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {integration.name}
          </Typography>
          <Chip
            label={getStatusLabel(integration.status)}
            color={getStatusColor(integration.status)}
            size="small"
          />
        </Box>

        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {integration.description}
        </Typography>

        <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onLearnMore(integration)}
            sx={{ flex: 1 }}
          >
            Learn More
          </Button>
          {integration.status === 'available' && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onGetStarted(integration)}
              sx={{ flex: 1 }}
            >
              Get Started
            </Button>
          )}
          {(integration.status === 'connected' ||
            integration.status === 'disabled') && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => onGetStarted(integration)}
              sx={{ flex: 1 }}
            >
              Manage
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
